import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, X, Sprout, Shield, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: '', label: 'Todas as categorias' },
  { value: 'sementes', label: 'Sementes' },
  { value: 'defensivos', label: 'Defensivos' },
  { value: 'fertilizantes', label: 'Fertilizantes' },
  { value: 'agricultura_precisao', label: 'Agricultura de precisão' },
];

const CATEGORY_COLORS = {
  sementes: 'bg-green-100 text-green-800',
  defensivos: 'bg-blue-100 text-blue-800',
  fertilizantes: 'bg-yellow-100 text-yellow-800',
  agricultura_precisao: 'bg-purple-100 text-purple-800',
};

const SECTIONS = [
  { key: 'sementes', label: 'SEMENTES', icon: Sprout, iconBg: 'bg-green-100', iconColor: 'text-green-600', accent: 'border-green-600' },
  { key: 'defensivos', label: 'DEFENSIVOS', icon: Shield, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', accent: 'border-blue-600' },
  { key: 'fertilizantes', label: 'FERTILIZANTES', icon: Zap, iconBg: 'bg-yellow-100', iconColor: 'text-yellow-600', accent: 'border-yellow-500' },
];

const SUBCATEGORIES_BY_CATEGORY = {
  defensivos: ['Herbicida', 'Fungicida', 'Inseticida', 'Acaricida', 'Nematicida', 'Adjuvante'],
  sementes: ['Soja', 'Milho', 'Algodão', 'Café'],
  fertilizantes: ['Foliar', 'Granulado', 'Líquido', 'Bioestimulante', 'Inoculante'],
};

const matchesSubcategory = (product, subKey) => {
  const classe = (product.classe_agronomica || '').toLowerCase();
  const key = subKey.toLowerCase();
  if (!classe) return false;
  if (key === 'herbicida') return classe.includes('herbic');
  if (key === 'fungicida') return classe.includes('fungic');
  if (key === 'inseticida') return classe.includes('insetic');
  if (key === 'acaricida') return classe.includes('acaric');
  if (key === 'nematicida') return classe.includes('nematic');
  if (key === 'adjuvante') return classe.includes('adjuvante');
  if (key === 'foliar') return classe.includes('foliar');
  if (key === 'granulado') return classe.includes('granulado');
  if (key === 'líquido' || key === 'liquido') return classe.includes('líquido') || classe.includes('liquido');
  if (key === 'bioestimulante') return classe.includes('bioestim');
  if (key === 'inoculante') return classe.includes('inoculante');
  return false;
};

const matchesSeedCrop = (product, crop) => {
  const text = `${product.name || ''} ${product.description || ''}`.toLowerCase();
  return text.includes(crop.toLowerCase());
};

const ProductCard = ({ product, getCategoryLabel }) => (
  <Link
    to={`/produtos/${product.slug}`}
    data-testid={`product-card-${product.slug}`}
    className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
  >
    <div className="h-48 relative flex items-center justify-center p-4" style={{ backgroundColor: '#0072D6' }}>
      {product.image_url ? (
        <img src={product.image_url} alt={product.name} className="max-h-full max-w-full object-contain" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <span className="text-4xl font-bold text-white">{product.name.charAt(0)}</span>
        </div>
      )}
      <div
        className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-semibold ${
          CATEGORY_COLORS[product.category] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {getCategoryLabel(product.category)}
      </div>
    </div>
    <div className="p-4">
      <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">{product.name}</h3>
      {product.classe_agronomica && (
        <p className="text-sm text-gray-600 mb-2">
          <span className="font-semibold">Classe:</span> {product.classe_agronomica}
        </p>
      )}
      {product.description && <p className="text-sm text-gray-500 line-clamp-2">{product.description}</p>}
      <div className="mt-4">
        <span className="text-green-600 font-semibold text-sm hover:text-green-700">Ver detalhes →</span>
      </div>
    </div>
  </Link>
);

const Produtos = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedCategory = searchParams.get('categoria') || '';
  const selectedSubcategory = searchParams.get('classe') || '';

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setProducts([]);
        const url = selectedCategory ? `${API}/products?category=${selectedCategory}` : `${API}/products`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        console.error('Erro ao buscar produtos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key === 'categoria') {
      next.delete('classe');
    }
    setSearchParams(next, { replace: true });
  };

  const availableSubcategories = SUBCATEGORIES_BY_CATEGORY[selectedCategory] || [];

  const getCategoryLabel = (category) => {
    const cat = CATEGORIES.find((c) => c.value === category);
    return cat ? cat.label : category;
  };

  const searchFiltered = useMemo(() => {
    if (!searchTerm) return products;
    const q = searchTerm.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(q));
  }, [products, searchTerm]);

  // Modo FILTRADO: usuário selecionou uma categoria
  const filteredByCategory = useMemo(() => {
    if (!selectedSubcategory) return searchFiltered;
    if (selectedCategory === 'sementes') {
      return searchFiltered.filter((p) => matchesSeedCrop(p, selectedSubcategory));
    }
    return searchFiltered.filter((p) => matchesSubcategory(p, selectedSubcategory));
  }, [searchFiltered, selectedSubcategory, selectedCategory]);

  // Modo SEÇÕES: agrupa produtos por categoria quando não há filtro
  const productsBySection = useMemo(() => {
    const grouped = { sementes: [], defensivos: [], fertilizantes: [] };
    for (const p of searchFiltered) {
      if (grouped[p.category]) grouped[p.category].push(p);
    }
    return grouped;
  }, [searchFiltered]);

  const showSections = !selectedCategory;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section
        className="relative py-20 text-white"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(https://customer-assets.emergentagent.com/job_primeiras-paginas/artifacts/5qaraoip_Design%20sem%20nome%20%2817%29.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">CATÁLOGO DE PRODUTOS</h1>
          <p className="text-base md:text-lg max-w-3xl mx-auto">
            Conheça nossa linha completa de produtos agrícolas
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                data-testid="produtos-search-input"
                placeholder="Buscar produtos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                data-testid="produtos-category-select"
                value={selectedCategory}
                onChange={(e) => updateParam('categoria', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent appearance-none"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {availableSubcategories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="subcategory-chips">
              <span className="text-sm text-gray-500 mr-1">Filtrar por classe:</span>
              <button
                type="button"
                onClick={() => updateParam('classe', '')}
                data-testid="chip-todas"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  !selectedSubcategory
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-600 hover:text-blue-600'
                }`}
              >
                Todas
              </button>
              {availableSubcategories.map((sub) => {
                const active = selectedSubcategory === sub;
                return (
                  <button
                    key={sub}
                    type="button"
                    onClick={() => updateParam('classe', active ? '' : sub)}
                    data-testid={`chip-${sub.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1 ${
                      active
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-600 hover:text-blue-600'
                    }`}
                  >
                    {sub}
                    {active && <X className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Carregando produtos...</p>
            </div>
          ) : showSections ? (
            // ============ MODO SEÇÕES (sem filtro de categoria) ============
            <div className="space-y-16" data-testid="sections-mode">
              {SECTIONS.map((section) => {
                const list = productsBySection[section.key] || [];
                if (list.length === 0) return null;
                const Icon = section.icon;
                return (
                  <div key={section.key} data-testid={`section-${section.key}`}>
                    <div className={`flex items-center gap-3 md:gap-4 mb-6 md:mb-8 pb-4 border-b-4 ${section.accent}`}>
                      <div className={`${section.iconBg} w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-6 h-6 md:w-7 md:h-7 ${section.iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{section.label}</h2>
                        <p className="text-sm text-gray-500">{list.length} produto(s)</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateParam('categoria', section.key)}
                        data-testid={`section-${section.key}-filter-btn`}
                        className="hidden md:inline-block text-sm font-semibold text-blue-600 hover:text-blue-700 border border-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors"
                      >
                        Filtrar somente {section.label.toLowerCase()} →
                      </button>
                    </div>
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {list.map((product) => (
                        <ProductCard key={product.id} product={product} getCategoryLabel={getCategoryLabel} />
                      ))}
                    </div>
                  </div>
                );
              })}

              {Object.values(productsBySection).every((list) => list.length === 0) && (
                <div className="text-center py-20" data-testid="empty-state">
                  <p className="text-2xl text-gray-600">Nenhum produto encontrado</p>
                  <p className="text-gray-500 mt-2">Tente ajustar os filtros de busca</p>
                </div>
              )}
            </div>
          ) : filteredByCategory.length === 0 ? (
            // ============ MODO FILTRADO — Vazio ============
            <div className="text-center py-20" data-testid="empty-state">
              <p className="text-2xl text-gray-600">Nenhum produto encontrado</p>
              <p className="text-gray-500 mt-2">Tente ajustar os filtros de busca</p>
            </div>
          ) : (
            // ============ MODO FILTRADO — Lista única ============
            <>
              <p className="text-gray-600 mb-6" data-testid="product-count">
                {filteredByCategory.length} produto(s) encontrado(s) em{' '}
                <span className="font-semibold text-gray-800">{getCategoryLabel(selectedCategory)}</span>
                {selectedSubcategory && (
                  <>
                    {' '}/ <span className="font-semibold text-blue-700">{selectedSubcategory}</span>
                  </>
                )}
              </p>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredByCategory.map((product) => (
                  <ProductCard key={product.id} product={product} getCategoryLabel={getCategoryLabel} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Produtos;
